import { Button } from '@/components/ui/button';
import { IssueBoard } from '../components/issues/board/IssueBoard';
import { IssueBoardCard } from '../components/issues/board/IssueBoardCard';
import { IssueBoardControls, IssueViewToggle } from '../components/issues/board/IssueBoardControls';
import { useIssueBoardPreferences } from '../components/issues/board/useIssueBoardPreferences';
import { BULK_MAX_IDS_PER_REQUEST } from '@overlayqa/shared';
import { collectIssueLabels } from '@overlayqa/shared';
import { useState, useMemo, useRef, useEffect } from 'react';
import { newUuid, verificationHandoffUrl } from '@overlayqa/shared';
import { ISSUE_TYPE_FILTER_OPTIONS } from '../lib/issueTypeOptions';
import { toast } from 'sonner';
import { HugeiconsIcon } from '@hugeicons/react';
import { Settings02Icon, CheckmarkCircle01Icon, Flag03Icon, UserIcon, Layers01Icon, UserEdit01Icon, Tag01Icon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useProject, useProjects } from '../hooks/useProjects';
import { useTeamMembers } from '../hooks/useTeams';
import { useProjectNotFoundRedirect, isProjectNotFound } from '../hooks/useProjectNotFoundRedirect';
import { useExtensionDetection } from '../hooks/useExtensionDetection';
import { DemoBanner } from '../components/dashboard/DemoBanner';
import { ProjectChartsSection } from '../components/projects/ProjectChartsSection';
import { CaptureIssuesButton } from '../components/issues/CaptureIssuesButton';
import { canWriteProject } from '../lib/projectPermissions';
import { useCaptureIssues } from '../hooks/useCaptureIssues';
import { resolveCaptureIssuesUrl } from '../lib/captureIssuesUrl';
import { ClientReviewSection } from '../components/projects/ClientReviewSection';
import { ProjectSettingsModal } from '../components/projects/settings/ProjectSettingsModal';
import { InviteTeamButton } from '../components/projects/InviteTeamButton';
import { DataTable } from '../components/shared/DataTable';
import { DeletionConfirmDialog } from '@/components/shared/DeletionConfirmDialog';
import { AddScanIssuesModal } from '../components/issues/AddScanIssuesModal';
import { MoveIssuesDialog } from '../components/issues/MoveIssuesDialog';
import { IssuesEmptyState } from '../components/issues/IssuesEmptyState';
import { IssueListEmptyState } from '@overlayqa/shared/ui';
import { IssuesLoadError, IssuesRefreshError } from '../components/shared/IssuesLoadError';
import { NetworkError } from '../lib/api/networkError';
import { isTimeoutError } from '../lib/api/requestTimeout';
import { useUser } from '../lib/auth';
import { IssueFilters } from '../components/issues/IssueFilters';
import { useIssuesPageData, ACTIVE_STATUS_FILTER } from './issues/useIssuesPageData';
import { disarmIssueArrivals, peekIssueArrivals } from '../hooks/issueArrivals';
import { useInlineIssueUpdate } from '../hooks/useInlineIssueUpdate';
import { useRememberedIssueTypes } from './issues/useRememberedIssueTypes';
import { buildIssueColumns, issuePath, buildIssueRowActions, buildIssueBulkActions, buildExportSubmenu, COPY_LABEL } from './issues/issuesPageColumns';
import { copyIssues, type CopyIssuesMenu } from './issues/copyIssues';
import type { ClipboardFormat, ExportProvider } from '@overlayqa/shared';
import { isVerifyEnabled } from '../lib/verifyFlag';
import { BulkVerifyDialog } from '../routes/issues/BulkVerifyDialog';
import { BulkVerifyProgress } from '../routes/issues/BulkVerifyProgress';
import { useAuth } from '../lib/auth';
import { verifyApi } from '../lib/api/verify';
import { capture } from '../lib/analytics';
import { log } from '../lib/logger';

// Labels verbatim from the single-selects these replaced (2026-09-24).
const STATUS_FILTER_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'verified', label: 'Verified' },
  { value: 'closed', label: 'Closed' },
];
const SEVERITY_FILTER_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

export function IssuesPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const boardPreferences = useIssueBoardPreferences(projectId || '');
  const { data: project, error: projectError } = useProject(projectId || '');
  // Display the approved "Title - detail" treatment without changing the
  // saved name. Project.name is the single field written by GeneralTab.
  const nameSeparator = project?.name.indexOf(' - ') ?? -1;
  const nameTitle = nameSeparator > 0 ? project?.name.slice(0, nameSeparator).trim() : '';
  const nameDetail = nameSeparator >= 0 ? project?.name.slice(nameSeparator + 3).trim() : '';
  const splitName = nameSeparator > 0 && nameTitle && nameDetail;
  const projectTitle = splitName ? nameTitle : project?.name;
  const projectDetail = splitName ? nameDetail : null;
  const { data: members, isLoading: membersLoading, error: membersError, refetch: refetchMembers } = useTeamMembers(project?.teamId);
  const teamMembers = useMemo(() => (members ?? []).flatMap(member => member.user
    ? [{ id: member.user.id, name: member.user.name || member.user.email, avatarUrl: member.user.avatarUrl }]
    : []), [members]);
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [showClientReviews, setShowClientReviews] = useState(false);
  const { detected: extensionDetected, checking: extensionChecking } = useExtensionDetection();
  const captureIssues = useCaptureIssues({ extensionDetected });
  const canCapture = canWriteProject(project?.role);
  // Destination candidates for "Move to project". Same query the Add-scan modal
  // uses, so the list is already scoped to the active team — which is also the
  // only scope the server will accept (it refuses a cross-team destination).
  const { data: teamProjects } = useProjects(project?.teamId);
  const [moveTarget, setMoveTarget] = useState<{ ids: string[] } | null>(null);

  // Mirrors requireProjectWrite (packages/server/src/middleware/auth.ts:733-739),
  // which refuses only `!role || role === 'viewer'`: owner, collaborator, and
  // team can all write, so the door to Project Settings must open for all
  // three. A collaborator was previously permitted by the server to link a
  // Figma file but had no click path to the control that does it. The tabs
  // inside gate themselves (projectSettingsTabs.ts: Sharing and Danger Zone
  // owner-only), so widening the door exposes nothing they do not permit.
  const canOpenProjectSettings = project?.role == null || project?.role !== 'viewer';

  // Inline cell editing follows the SAME rule as the Project Settings door
  // above and as the server's own PATCH /issues/:id, which refuses `!role ||
  // role === 'viewer'` (routes/issues/update.ts:66). Three doors on one screen,
  // one answer to "may this person write", so they cannot disagree. This is the
  // courtesy half: the server refuses a viewer's write regardless.
  const canEditIssuesInline = project?.role == null || project?.role !== 'viewer';

  // Project Settings is a modal driven by ?settings=<tab> (2026-09-17). The
  // retired /projects/:id/settings route redirects here with it, so a deep
  // link, a reload and Back all land on the right tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const settingsTab = searchParams.get('settings');
  const setSettingsParam = (tab: string | null, replace: boolean) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === null) next.delete('settings');
        else next.set('settings', tab);
        return next;
      },
      { replace },
    );
  // A push, not a replace: Back closes the modal.
  const openProjectSettings = () => setSettingsParam('general', false);

  const {
    searchQuery, setSearchQuery,
    statusFilter, setStatusFilter,
    severityFilter, setSeverityFilter,
    typeFilter, setTypeFilter,
    assigneeFilter, setAssigneeFilter,
    creatorFilter, setCreatorFilter,
    labelFilter, setLabelFilter,
    hiddenFinishedCount, hasFilters, hasChoiceFilters, clearFilters,
    confirmDelete, setConfirmDelete,
    issues, allIssues,
    ticketsData,
    isFetching, error, refetch,
    showSkeleton,
    bulkDelete, bulkStatus, bulkIgnore, moveIssues,
    availableProviders,
    handleExport,
    handleDelete,
  } = useIssuesPageData(projectId, boardPreferences.view);

  // ── Inline cell editing ──────────────────────────────────────────────────
  // A pick saves at once: the badge changes, the request settles behind it, and
  // a refusal puts the old value back with the same sentence the detail page
  // shows (useInlineIssueUpdate). There is no Save button because the row's own
  // Change status action already saves instantly, and the badge changing IS the
  // confirmation. Rendered behaviour: e2e/verify-inline-cell-edit.ts.
  const inlineUpdate = useInlineIssueUpdate();
  const rememberedTypeOf = useRememberedIssueTypes(issues);
  const issueColumnDefs = useMemo(
    () => buildIssueColumns({
          teamMembers,
          membersLoading: membersLoading || !project,
          membersError: !!membersError,
          onAssigneeChange: project && canEditIssuesInline
            ? (issue, assigneeId) => inlineUpdate.mutateAsync({ id: issue.id, data: { assigneeId } })
            : undefined,
          alsoOfferType: rememberedTypeOf,
          // issue_status_changed is announced by the server PATCH handler for
          // every surface (services/issueStatusAnalytics.ts), never here.
          onFieldChange: canEditIssuesInline ? (issue, data, via) => inlineUpdate.mutate({ id: issue.id, data, via }) : undefined,
        }),
    [canEditIssuesInline, rememberedTypeOf, inlineUpdate, teamMembers, membersLoading, membersError, project],
  );
  // Every filter pick reports what it narrowed to; before 2026-09-24 only the
  // creator filter emitted, so the other four step up from zero here.
  const applyFilter = (filterType: string, value: string[], set: (v: string[]) => void) => {
    capture('issue_filter_applied', { filter_type: filterType, value: value.length ? value.join(',') : 'all', count: value.length, surface: 'dashboard' });
    set(value);
  };
  const showAllStatuses = () => applyFilter('status', STATUS_FILTER_OPTIONS.map((o) => o.value), setStatusFilter);

  // For the load-error state: naming the signed-in account makes the
  // wrong-account case (email link opened in another profile) self-evident.
  const { user: authUser } = useUser();
  const signedInEmail = authUser?.primaryEmailAddress?.emailAddress ?? null;

  // Recovery for a deleted / missing project in the URL: either the project
  // fetch (/projects/:id) or the issues fetch (/issues?projectId=) surfaces the
  // 404. Redirect off the dead route once instead of stranding the user on a
  // retrying error card (incident 2026-07-13 — a real signup deleted their demo
  // with its issues tab open, and it re-404'd on every refocus for hours).
  const notFoundError = isProjectNotFound(projectError)
    ? projectError
    : isProjectNotFound(error)
      ? error
      : null;
  // Pass the URL projectId so a self-delete (this project, from its own Settings)
  // redirects silently instead of stacking "This project no longer exists." on
  // top of the delete handler's "Project deleted" success.
  useProjectNotFoundRedirect(notFoundError, '/projects', projectId);

  // Bulk-verify state (Task 5.8).
  // - verifySelectedIds: which issue ids the dialog is configured for
  // - showVerifyDialog: whether the BulkVerifyDialog modal is mounted
  // - startingJob: createBulkJob() in flight; disables the Start button
  // - activeJobId: id of the running job, drives the BulkVerifyProgress strip
  const [verifySelectedIds, setVerifySelectedIds] = useState<string[]>([]);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [startingJob, setStartingJob] = useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  // Rows that just arrived from "Add N issues", so the table can land them one
  // after another. Read on the render where the list CHANGES: takeIssueArrivals
  // stays quiet unless a create armed it, so a filter or page change is silent.
  const previousIssueIds = useRef<string[]>([]);
  /** False until this page has shown a list once. A create that lands in
   *  another project navigates, remounting this page with no previous list. */
  const hasShownList = useRef(false);
  const issueIds = useMemo(() => issues.map((i) => i.id), [issues]);
  // PURE: StrictMode double-invokes this, and both invocations must agree.
  const cascadeArrivals = useMemo(
    () => peekIssueArrivals(previousIssueIds.current, issueIds, Date.now(), hasShownList.current),
    [issueIds],
  );
  // The mutations live here, where a double-run is idempotent. A first load
  // CONSUMES the window without cascading: leaving it armed would spend it on
  // the next filter change instead.
  useEffect(() => {
    const firstLoad = !hasShownList.current;
    previousIssueIds.current = issueIds;
    hasShownList.current = true;
    if (firstLoad || cascadeArrivals.length > 0) disarmIssueArrivals();
  }, [issueIds, cascadeArrivals]);

  // Quick lookup so BulkAction.isDisabled / dialog can resolve full issue
  // objects from the array of selected ids without re-scanning the list.
  const ignoredCount = useMemo(() => issues.filter((i) => i.ignoredAt).length, [issues]);
  const issuesById = useMemo(() => {
    const map = new Map<string, typeof issues[number]>();
    for (const issue of issues) map.set(issue.id, issue);
    return map;
  }, [issues]);

  const handleVerifyClick = (ids: string[]) => {
    log.info('[IssuesPage] Verify fixes clicked', { count: ids.length });
    setVerifySelectedIds(ids);
    setShowVerifyDialog(true);
  };

  const verifyDialogIssues = useMemo(
    () =>
      verifySelectedIds.map((id) => {
        const issue = issuesById.get(id);
        return {
          id,
          title: issue?.title ?? 'Unknown issue',
          pageUrl: issue?.pageUrl ?? null,
        };
      }),
    [verifySelectedIds, issuesById],
  );

  const handleStartBulkVerify = async () => {
    if (verifySelectedIds.length === 0) return;
    setStartingJob(true);
    try {
      const token = await getToken();
      const job = await verifyApi.createBulkJob(token ?? undefined, {
        issueIds: verifySelectedIds,
        source: 'dashboard_bulk',
        idempotencyKey: newUuid(),
      });
      log.info('[IssuesPage] Bulk verify job created', {
        jobId: job.jobId,
        totalIssues: job.totalIssues,
        urlCount: Object.keys(job.urlGroups).length,
      });
      setShowVerifyDialog(false);
      setActiveJobId(job.jobId);

      // Hand off to the extension via fragment URL. The user clicking
      // "Start verify" is their explicit consent for this navigation
      // (per spec §8.3 / plan decision #8 — no magic auto-detection).
      const firstUrl = Object.keys(job.urlGroups)[0];
      if (firstUrl) {
        window.open(verificationHandoffUrl(firstUrl, 'job', job.jobId), '_blank', 'noopener,noreferrer');
      }
      capture('verify_bulk_started', {
        job_id: job.jobId,
        source: 'dashboard_bulk',
        issue_count: job.totalIssues,
        url_groups: Object.keys(job.urlGroups).length,
      });
    } catch (err: unknown) {
      log.error('[IssuesPage] Failed to start bulk verify', { error: err });
      // Dialog stays open so the user can retry; loading state clears below.
      toast.error("Couldn't start verification. Please try again.");
    } finally {
      setStartingJob(false);
    }
  };

  // Capture issues: the URL the team review opens at, resolved from what the
  // project already holds (newest issue's page, scan URL, primary domain;
  // lib/captureIssuesUrl.ts). A reachable URL opens the review at once with
  // no modal; only an unknown or proxy-unreachable URL still asks
  // (useCaptureIssues). `ticketsData.items` is the list as the user filtered
  // it, so "newest" is judged by createdAt inside the resolver, never by
  // position.
  const resolvedCaptureUrl = useMemo(
    () => resolveCaptureIssuesUrl({ issues: ticketsData?.items, project }),
    [ticketsData?.items, project],
  );
  const startCapture = (surface: 'issues_toolbar' | 'issues_empty_state') => {
    if (!projectId || !canCapture) return;
    captureIssues.start({ projectId, resolved: resolvedCaptureUrl, surface });
  };
  const openCaptureModal = () => {
    if (!projectId || !canCapture) return;
    captureIssues.openModal({ projectId, initialUrl: resolvedCaptureUrl.url ?? undefined, surface: 'issues_toolbar' });
  };

  const exportSubmenuForIds = (ids: string[]) =>
    buildExportSubmenu(availableProviders, ids, (exportIds, provider) => {
      handleExport(exportIds, provider as ExportProvider);
    });

  const openMoveDialog = (ids: string[]) => setMoveTarget({ ids });

  // Copy for saved issues (2026-10-25): `issues` is the table's data in render
  // order, so the copy keeps the order the user sees. Writes nothing to the
  // server, so it is offered to read-only viewers as well.
  const copyListedIssues = (ids: string[], format: ClipboardFormat, menu: CopyIssuesMenu) => {
    void copyIssues(issues, ids, format, menu, getToken, Boolean(authUser));
  };

  const rowActions = useMemo(
    () => buildIssueRowActions({ bulkStatus, bulkIgnore, availableProviders, exportSubmenuForIds, setConfirmDelete, onMoveClick: openMoveDialog, onCopy: copyListedIssues }),
    [bulkStatus, bulkIgnore, availableProviders, handleExport, issues, getToken, authUser],
  );

  const bulkActions = useMemo(
    () => buildIssueBulkActions({
      bulkStatus,
      bulkIgnore,
      availableProviders,
      exportSubmenuForIds,
      setConfirmDelete,
      issuesById,
      onVerifyClick: handleVerifyClick,
      verifyEnabled: isVerifyEnabled(),
      onMoveClick: openMoveDialog,
      onCopy: copyListedIssues,
    }),
    [bulkStatus, bulkIgnore, availableProviders, handleExport, issuesById, issues, getToken, authUser],
  );

  // The project 404'd — the redirect effect above is navigating to /projects.
  // Render nothing rather than flash the skeleton or the IssuesLoadError card.
  if (notFoundError) return null;

  if (showSkeleton) {
    const rowWidths = [70, 55, 80, 45, 75, 60, 85];
    return (
      <section className="page-content" aria-busy="true" aria-label="Loading issues">
        <header className="page-header flex items-center gap-3 mb-4" aria-hidden="true">
          <div className="min-w-0 flex-1">
            <div className="h-8 w-64 max-w-full rounded skeleton-shimmer" />
            <div className="mt-2 h-4 w-40 max-w-full rounded skeleton-shimmer" />
          </div>
          <div className="ml-auto w-20 h-8 rounded-lg skeleton-shimmer" />
        </header>
        <div className="flex gap-3 mb-6">
          <div className="h-8 w-48 rounded skeleton-shimmer" />
          <div className="h-8 w-32 rounded skeleton-shimmer" />
          <div className="h-8 w-32 rounded skeleton-shimmer" />
          <div className="h-8 w-32 rounded skeleton-shimmer" />
        </div>
        <div className="bg-white rounded-lg border border-[var(--color-card-stroke)] overflow-hidden">
          <div className="bg-gray-50/50 border-b border-gray-100 flex gap-4 px-4 py-3">
            <div className="w-4 h-3 rounded skeleton-shimmer flex-shrink-0" />
            <div className="flex-1 h-3 w-16 rounded skeleton-shimmer" />
            <div className="w-16 h-3 rounded skeleton-shimmer" />
            <div className="w-16 h-3 rounded skeleton-shimmer" />
            <div className="w-16 h-3 rounded skeleton-shimmer" />
            <div className="w-16 h-3 rounded skeleton-shimmer" />
          </div>
          {rowWidths.map((w, i) => {
            const delay = `${i * 80}ms`;
            return (
              <div key={i} className={`flex items-center gap-4 px-4 py-3 ${i % 2 === 1 ? 'bg-gray-50/60' : ''}`}>
                <div className="w-4 h-4 rounded skeleton-shimmer flex-shrink-0" style={{ animationDelay: delay }} />
                <div className="flex-1 min-w-0">
                  <div className="h-4 rounded skeleton-shimmer" style={{ width: `${w}%`, animationDelay: delay }} />
                </div>
                <div className="w-14 h-5 rounded-full skeleton-shimmer flex-shrink-0" style={{ animationDelay: delay }} />
                <div className="w-20 h-5 rounded-full skeleton-shimmer flex-shrink-0" style={{ animationDelay: delay }} />
                <div className="w-16 h-5 rounded-full skeleton-shimmer flex-shrink-0" style={{ animationDelay: delay }} />
                <div className="w-20 h-4 rounded skeleton-shimmer flex-shrink-0" style={{ animationDelay: delay }} />
                <div className="w-5 h-5 rounded skeleton-shimmer flex-shrink-0" style={{ animationDelay: delay }} />
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  // useIssuesPageData keeps the same query's successful data on a refetch
  // error (pages/issues/useIssuesPageData.ts:61). Keep its table mounted so
  // a connection failure does not discard the user's selection. Only these
  // transport errors qualify; access refusals still replace the loaded list.
  const showCachedIssues = ticketsData !== undefined
    && (error instanceof NetworkError || isTimeoutError(error));
  if (error && !showCachedIssues) {
    return (
      <section className="page-content">
        <IssuesLoadError
          error={error}
          signedInEmail={signedInEmail}
          onRetry={() => refetch()}
        />
      </section>
    );
  }

  const issueEmptyState = allIssues.length > 0 ? (
    <IssueListEmptyState totalCount={allIssues.length} searchQuery={searchQuery} hasFilters={hasChoiceFilters}
      hiddenFinishedCount={hiddenFinishedCount} onShowAllStatuses={showAllStatuses}
      onClearSearch={() => setSearchQuery('')} onClearFilters={clearFilters} />
  ) : (
    <IssuesEmptyState extensionDetected={extensionDetected} extensionChecking={extensionChecking}
      readOnly={project?.role === 'viewer'}
      onCapture={projectId && canCapture ? () => startCapture('issues_empty_state') : undefined}
      targetUrl={resolvedCaptureUrl.url} />
  );

  return (
    <section id="issues" className="page-content">
      {/* The top-nav keeps the project switcher; this heading gives the page
          its full project context. Settings still opens its modal in place. */}
      {project && (
        <header className="page-header flex flex-wrap items-center gap-x-3 gap-y-4 mb-4">
          <div className="min-w-0 basis-full sm:basis-0 sm:flex-1">
            <h1 className="hero-title [overflow-wrap:anywhere]">{projectTitle}</h1>
            <nav className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500" aria-label="Project context">
              {projectDetail && <><span className="min-w-0 [overflow-wrap:anywhere]">{projectDetail}</span><span aria-hidden="true" className="text-gray-400">/</span></>}
              <span aria-current="page">Issues</span>
            </nav>
          </div>
          {project?.role === 'collaborator' && (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200">
              Shared
            </span>
          )}
          {project?.role === 'viewer' && (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
              View only
            </span>
          )}
          {project?.role === 'team' && (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-50 text-green-700 border border-green-200">
              Team
            </span>
          )}
          {/* Ignore for issues (2026-09-22): how many rows in this list are
              parked. The gray chip family the role chips use; absent at zero. */}
          {ignoredCount > 0 && (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200" title="Ignored issues in this list">
              {ignoredCount} ignored
            </span>
          )}
          {/* Right-aligned actions above mobile: "Invite team", then Settings.
              The pair shares ONE wrapper: on Settings alone ml-auto would strand "Invite team"
              at the far left, and on "Invite team" it would strand Settings
              whenever that button hides itself (plain members, shared projects). Settings
              reads gray-500, not gray-400: measured 2026-09-18, gray-400 is 3.08:1
              on white and 2.93:1 on the gray-50 page, under the 4.5:1 AA floor
              for 14px text; gray-500 is 4.83 / 4.59. "Invite team" is the blue one
              (primary-600, 6.42 / 6.09; Emily 2026-09-22), so the action of the
              pair reads as the action. */}
          <div className="flex shrink-0 items-center gap-1 sm:ml-auto">
            <InviteTeamButton projectTeamId={project.teamId} />
            {/* Settings gear beside the project heading. Stays a <button> on purpose:
                three harnesses select it by role, and its aria-label is the
                contract they assert on. */}
            {canOpenProjectSettings && project && (
              <button
                type="button"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={openProjectSettings}
                aria-label="Open project settings"
                title="Project settings"
              >
                <HugeiconsIcon icon={Settings02Icon} size={18} strokeWidth={2} color="currentColor" className="w-[18px] h-[18px]" aria-hidden="true" />
                <span className="text-cap-trim">Settings</span>
              </button>
            )}
          </div>
        </header>
      )}

      <>
          {/* New signups land here inside the seeded demo project, so the
              "this is a sample" explanation belongs above the charts, before
              the pre-made issues read as their own work. Same placement the
              (unrouted) TicketsPage used. */}
          {project?.isDemo && projectId && <DemoBanner projectId={projectId} />}

          {projectId && <ProjectChartsSection projectId={projectId} />}

          {/* Search + Filters */}
          <div className="list-toolbar">
          <IssueFilters key={projectId} searchQuery={searchQuery} onSearchChange={setSearchQuery} hasFilters={hasFilters} onClear={clearFilters}
            groups={[
              { name: 'Status', icon: CheckmarkCircle01Icon, idleLabel: boardPreferences.view === 'board' ? 'All statuses' : 'Active issues', 'aria-label': 'Filter by status', alwaysVisible: true,
                options: STATUS_FILTER_OPTIONS, defaultValue: boardPreferences.view === 'board' ? STATUS_FILTER_OPTIONS.map(option => option.value) : ACTIVE_STATUS_FILTER,
                presets: [{ label: 'Active issues', value: ACTIVE_STATUS_FILTER }, { label: 'All statuses', value: STATUS_FILTER_OPTIONS.map(option => option.value) }],
                resetLabel: boardPreferences.view === 'board' ? 'Show all statuses' : 'Reset to active issues',
                value: statusFilter, onChange: value => applyFilter('status', value, setStatusFilter) },
              { name: 'Severity', icon: Flag03Icon, alwaysVisible: true, idleLabel: 'All severity', 'aria-label': 'Filter by severity', options: SEVERITY_FILTER_OPTIONS,
                value: severityFilter, onChange: value => applyFilter('severity', value, setSeverityFilter) },
              { name: 'Type', icon: Layers01Icon, idleLabel: 'All types', 'aria-label': 'Filter by type', options: ISSUE_TYPE_FILTER_OPTIONS,
                value: typeFilter, onChange: value => applyFilter('type', value, setTypeFilter) },
              { name: 'Assignee', icon: UserIcon, alwaysVisible: true, idleLabel: 'All assignees', 'aria-label': 'Filter by assignee', disabled: membersLoading || !project,
                options: [{ value: 'unassigned', label: 'Unassigned' }, ...teamMembers.map(member => ({ value: member.id, label: member.name })),
                  ...assigneeFilter.filter(id => id !== 'unassigned' && !teamMembers.some(member => member.id === id)).map(id => ({ value: id, label: 'Assigned teammate' }))],
                value: assigneeFilter, onChange: value => applyFilter('assignee', value, setAssigneeFilter) },
              { name: 'Created by', icon: UserEdit01Icon, idleLabel: 'All creators', 'aria-label': 'Filter by creator', disabled: membersLoading || !project,
                options: [...teamMembers.map(member => ({ value: member.id, label: member.name })),
                  ...creatorFilter.filter(id => !teamMembers.some(member => member.id === id)).map(id => ({ value: id, label: 'Former teammate' }))],
                value: creatorFilter, onChange: value => applyFilter('creator', value, setCreatorFilter) },
              { name: 'Tags', icon: Tag01Icon, idleLabel: 'All tags', 'aria-label': 'Filter by tag',
                options: [{ value: 'unlabeled', label: 'No tags' }, ...collectIssueLabels(ticketsData?.items ?? []).map(label => ({ value: label.id, label: label.name }))],
                value: labelFilter, onChange: setLabelFilter },
            ]} />

            {/* Both Capture issues (team review) and Share client link are live
                on prod. Share client link opens the same management modal as the
                project action dropdown's "Share client link" item (see ProjectsPage). */}
            {projectId && (
              <div className="list-toolbar__actions">
                <div className="flex shrink-0 items-center gap-3">
                  <IssueViewToggle preferences={boardPreferences} />
                  {canCapture && <Button
                    type="button" variant="outline" context="toolbar" icon={PlusSignIcon}
                    className="whitespace-nowrap shrink-0"
                    onClick={() => setShowClientReviews(true)}
                  >
                    Share client link
                  </Button>}
                </div>
                {canCapture && <CaptureIssuesButton
                  onCapture={() => startCapture('issues_toolbar')}
                  onCaptureOther={openCaptureModal}
                  pending={captureIssues.pendingProjectId === projectId}
                  targetUrl={resolvedCaptureUrl.url}
                />}
              </div>
            )}
          </div>

          {/* Bulk-verify progress strip — mounts when a dashboard_bulk job is active.
              See packages/dashboard/src/routes/issues/BulkVerifyProgress.tsx for polling lifecycle. */}
          {activeJobId && (
            <BulkVerifyProgress
              jobId={activeJobId}
              onDismiss={() => setActiveJobId(null)}
              onViewResults={() => setActiveJobId(null)}
            />
          )}

          <IssueBoardControls preferences={boardPreferences} />
          {/* Issues Table */}
          {showCachedIssues && (
            <IssuesRefreshError retrying={isFetching} onRetry={() => refetch()} />
          )}
          {membersError && <p role="alert" className="mb-3 text-xs text-gray-600">Couldn’t load teammates. <button type="button" className="text-primary-600 underline" onClick={() => { void refetchMembers(); }}><span className="text-cap-trim block">Try again</span></button></p>}
          {boardPreferences.view === 'board' && issues.length === 0 && issueEmptyState}
          {boardPreferences.view === 'board' ? <IssueBoard key={projectId} projectId={projectId!}
            preferences={boardPreferences} issues={issues} allIssues={allIssues}
            readOnly={!project || !canEditIssuesInline || !!project.archivedAt}
            renderIssue={(issue, onMove, busy) => <IssueBoardCard issue={issue} members={teamMembers}
              actions={[
                {label:'Move to column',onClick:()=>onMove(issue),disabled:()=>busy || !project || !canEditIssuesInline || !!project.archivedAt,
                  disabledTitle:busy ? 'Wait for this move to finish.' : 'This project is view only.'},
                ...rowActions.filter(action=>action.label !== 'Change status').map(action=>({...action,
                  // Copy writes nothing, so a view-only or archived project keeps it (table parity).
                  disabled:(item:typeof issue)=>busy || (action.label !== COPY_LABEL && (!project || !canEditIssuesInline || !!project.archivedAt)) || !!action.disabled?.(item)})),
              ]} />}
          /> : <DataTable
            columns={issueColumnDefs}
            data={issues}
            cascadeArrivals={cascadeArrivals}
            rowKey={(issue) => issue.id}
            rowActions={rowActions}
            bulkActions={bulkActions}
            selectionLimit={BULK_MAX_IDS_PER_REQUEST}
            bulkActionPlacement="bottom"
            rowLabel={(issue) => issue.title}
            /* Ignore for issues (2026-09-22): the findings lists' 45% for an ignored row. */
            rowClassName={(issue) => (issue.ignoredAt ? 'opacity-45' : undefined)}
            /* Same destination as the row's own title link (issuePath), which
               is the only reason the row may carry a click at all. DataTable
               refuses the click when it lands on a link, ends a selection, or
               carries a modifier. */
            onRowClick={(issue) => navigate(issuePath(issue))}
            loading={bulkDelete.isPending || bulkStatus.isPending || bulkIgnore.isPending || (isFetching && !!ticketsData)}
            emptyState={issueEmptyState}
          />}

          {hiddenFinishedCount > 0 && (issues.length > 0 || boardPreferences.view === 'board') && (
            <p className="mt-3 text-xs text-gray-600">
              {hiddenFinishedCount} finished {hiddenFinishedCount === 1 ? 'issue' : 'issues'} hidden.{' '}
              <button type="button" className="font-medium text-primary-600 hover:text-primary-700" onClick={showAllStatuses}>
                <span className="text-cap-trim block">Show all statuses</span>
              </button>
            </p>
          )}

          <AddScanIssuesModal projectName={project?.name ?? 'this project'} />

          <MoveIssuesDialog
            open={moveTarget !== null}
            issueIds={moveTarget?.ids ?? []}
            currentProjectId={projectId ?? null}
            projects={teamProjects ?? []}
            moving={moveIssues.isPending}
            onCancel={() => setMoveTarget(null)}
            onMove={async (targetProjectId, targetProjectName) => {
              const ids = moveTarget?.ids ?? [];
              // Close first: the rows leave this list optimistically, so a
              // dialog still sitting over an emptied selection reads as a hang.
              setMoveTarget(null);
              try {
                await moveIssues.mutateAsync({ ids, targetProjectId, targetProjectName });
              } catch {
                // useMoveIssues already toasted the server's reason (including
                // the cross-team refusal). Swallow so the page does not unmount.
              }
            }}
          />

          <DeletionConfirmDialog
            open={confirmDelete !== null}
            request={confirmDelete ? { kind: 'issues', ids: confirmDelete.ids } : null}
            fallback={{
              title: 'Delete issues',
              message: `Delete ${confirmDelete?.ids.length ?? 0} issue${(confirmDelete?.ids.length ?? 0) === 1 ? '' : 's'}? This cannot be undone.`,
              confirmLabel: 'Delete',
            }}
            onConfirm={() => confirmDelete && handleDelete(confirmDelete.ids)}
            onCancel={() => setConfirmDelete(null)}
            loading={bulkDelete.isPending}
          />

          {showVerifyDialog && (
            <BulkVerifyDialog
              issues={verifyDialogIssues}
              onCancel={() => setShowVerifyDialog(false)}
              onStart={handleStartBulkVerify}
              loading={startingJob}
            />
          )}
        </>

      {canCapture && captureIssues.modal}

      {settingsTab !== null && canOpenProjectSettings && projectId && (
        <ProjectSettingsModal
          projectId={projectId}
          initialTab={settingsTab}
          onClose={() => setSettingsParam(null, true)}
          onTabChange={(tab) => setSettingsParam(tab, true)}
        />
      )}

      {showClientReviews && projectId && canCapture && (
        <ClientReviewSection
          projectId={projectId}
          onClose={() => setShowClientReviews(false)}
        />
      )}
    </section>
  );
}
